import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Style from "./style";

import MainHeader from "widget/MainHeader";

import GiveModal from "./ui/GiveModal";
import leftBlack from "./asset/left_black.svg";
import leftGrey from "./asset/left_grey.svg";
import rightBlack from "./asset/right_black.svg";
import rightGrey from "./asset/right_grey.svg";

import useGetEventDetail from "entity/event/useGetEventDetail";
import useDeleteEvent from "entity/event/useDeleteEvent";

import CStyle from "shared/style/commonStyle";
import TStyle from "shared/style/tableStyle";
import DTStyle from "shared/style/detailTableStyle";
import {
  OFFICIAL,
  PRELIMINARY,
  STORE,
  FREEROLL,
  convertEventTypeAsName,
} from "shared/constant/lookup/eventType";
import {
  ROLE_STORE_MANAGER,
  ROLE_FRANCHISE_MANAGER,
} from "shared/constant/lookup/role";
import { convertProGradeAsName } from "shared/constant/lookup/proGrade";
import { convertEventStateAsName } from "shared/constant/lookup/eventState";
import timestampCalculator from "shared/lib/timestampCalculator";
import useConfirmModalAtom from "shared/model/useConfirmModalAtom";
import useRoleCookie from "shared/model/useRoleCookie";

const EventDetail = () => {
  const navigate = useNavigate();
  const { idx } = useParams();
  const [userRole] = useRoleCookie();

  const tabList = ["게임 정보", "프라이즈 & 듀레이션"];
  const [tab, setTab] = useState(tabList[0]);
  const [imgIndex, setImgIndex] = useState(0);
  const [isGiveModalOpen, setIsGiveModalOpen] = useState(false);

  const [eventData] = useGetEventDetail();
  const [deleteEvent] = useDeleteEvent();
  const [, setModalEvent] = useConfirmModalAtom();

  const btnList = [];

  // 관리자 라면 추가
  if (![ROLE_STORE_MANAGER, ROLE_FRANCHISE_MANAGER].includes(userRole)) {
    btnList.push({
      label: "참가권 지급",
      clickEvent: () => setIsGiveModalOpen(true),
    });
  }

  [
    {
      label: "타이머 보기",
      clickEvent: () => {
        window.open(
          `${process.env.REACT_APP_TIMER_URL}?callback=event/${idx}/timer`,
          "_blank"
        );
      },
    },
    {
      label: "운영",
      clickEvent: () => navigate(`/event/${idx}/participant`),
    },
    {
      label: "수정",
      clickEvent: () => navigate(`/event/${idx}/edit`),
    },
    {
      label: "삭제",
      clickEvent: () =>
        setModalEvent("정말 삭제하시겠습니까?", () => deleteEvent),
    },
  ].forEach((elem) => btnList.push(elem));

  const eventDataList = [
    {
      label: "IDX",
      value: eventData?.data?.idx,
    },
    {
      label: "대회 이름",
      value: eventData?.data?.name,
    },
    {
      label: "대회 주소지",
      value: eventData?.data?.address,
    },
    {
      label: "대회 종류",
      value: convertEventTypeAsName[eventData?.data?.event_type_flag],
    },
    {
      label: "접수 시간",
      value: timestampCalculator(eventData?.data?.reg_date),
    },
    {
      label: "대회 시간",
      value: timestampCalculator(eventData?.data?.date),
    },
    {
      label: "요구 프로 자격",
      value: convertProGradeAsName[eventData?.data?.pro_grade_flag],
    },
    {
      label: "대회 상태",
      value: convertEventStateAsName[eventData?.data?.event_state_flag],
    },
    {
      label: "잔여 참가권",
      value: eventData?.data?.rest_entry ?? "제한 없음",
    },
    {
      label: "생성 날짜",
      value: timestampCalculator(eventData?.data?.created_at),
    },
  ];

  const seriesDataList = [
    {
      label: "IDX",
      value: eventData?.data?.series_idx,
    },
    {
      label: "대회 시리즈 이름",
      value: eventData?.data?.series_name,
    },
    {
      label: "시작 날짜",
      value: timestampCalculator(eventData?.data?.series_start_date, "date"),
    },
    {
      label: "종료 날짜",
      value: timestampCalculator(eventData?.data?.series_end_date, "date"),
    },
    {
      label: "생성 날짜",
      value: timestampCalculator(eventData?.data?.series_created_at),
    },
  ];

  const gameDataList = [
    {
      label: "스타팅 칩",
      value: eventData?.data?.starting_chip,
    },
    {
      label: "레지 마감 레벨",
      value: eventData?.data?.reg_end_level ?? "제한 없음",
    },
    {
      label: "얼리 마감 레벨",
      value: eventData?.data?.early_end_level ?? "얼리 없음",
    },
    {
      label: "얼리 칩",
      value: eventData?.data?.early_chip ?? "얼리 없음",
    },
    {
      label: "에드온 조건",
      value: eventData?.data?.add_on ?? "에드온 없음",
    },
    {
      label: "에드온 칩",
      value: eventData?.data?.add_on_chip ?? "에드온 없음",
    },
    {
      label: "리엔트리 횟수 제한",
      value:
        eventData?.data?.re_entry === 0
          ? "프리즈 아웃"
          : !eventData?.data?.re_entry
          ? "제한 없음"
          : eventData?.data?.re_entry,
    },
    {
      label: "리엔트리 칩",
      value:
        eventData?.data?.re_entry_chip?.length > 0
          ? eventData?.data?.re_entry_chip?.reduce(
              (acc, elem, index) => (acc += `${index + 1}회 (${elem}) `),
              ""
            )
          : "칩 추가 없음",
    },
    {
      label: "최대 엔트리",
      value: eventData?.data?.max_entry ?? "제한 없음",
    },
    {
      label: "총 프라이즈",
      value: eventData?.data?.prize_total,
    },
  ];

  const ticketDataList = [
    {
      label:
        eventData?.data?.event_type_flag === OFFICIAL
          ? "시드권 종류"
          : "매장 이용권 이름",
      value: eventData?.data?.ticket_name,
    },
    {
      label: "필요 개수",
      value: eventData?.data?.ticket_number,
    },
  ];

  const publishedTicketDataList = [
    {
      label: "발행 개수",
      value: eventData?.data?.published_ticket_number,
    },
    {
      label: "소진 개수",
      value: eventData?.data?.published_ticket_number,
    },
  ];

  const prizeSizeList = ["medium", "expand"];
  const prizeHeadList = ["순위", "프라이즈"];
  const prizeDataList = eventData?.data?.prize_list;

  const durationSizeList = ["expand", "medium", "medium", "medium", "medium"];
  const durationHeadList = ["Level", "Minute", "SB", "BB", "Ante"];
  const durationDataList = eventData?.data?.duration_list;

  const eventContactData = eventData?.data?.content;
  const eventImgList = eventData?.data?.img_list;
  const posterImg = eventData?.data?.poster_img;

  return (
    <>
      {isGiveModalOpen && (
        <GiveModal
          eventIdx={idx}
          closeEvent={() => setIsGiveModalOpen(false)}
        />
      )}

      <Style.Wrapper>
        <MainHeader btnList={btnList} />

        <div>
          <CStyle.GuideDiv>
            <p>대회의 상태를 변경하면 다시 이전 상태로 돌아갈 수 없습니다.</p>
          </CStyle.GuideDiv>
          <CStyle.GuideDiv>
            <p>대회 시작 버튼을 누르면 시작 프로세스가 진행됩니다.</p>
            <p>- 대회 상태를 '진행 중' 으로 변경</p>
            <p>- 듀레이션 타이머 시작</p>
          </CStyle.GuideDiv>
          <CStyle.GuideDiv>
            <p>대회 일시정지 버튼을 누르면 일시정지 프로세스가 진행됩니다.</p>
            <p>- 듀레이션 타이머 일시정지</p>
          </CStyle.GuideDiv>
          <CStyle.GuideDiv>
            <p>대회 종료 버튼을 누르면 종료 프로세스가 진행됩니다.</p>
            <p>- 대회 상태를 '종료' 로 변경</p>
            <p>- 듀레이션 타이머 정지 및 초기화</p>
            <p>
              - 현재 플레이어 순위에 따라 점수 부여 ( 꼭, 대회 종료 전 모든
              플레이어의 순위 점검 요망 )
            </p>
          </CStyle.GuideDiv>
        </div>

        <Style.AreaDiv>
          <h2>대회 데이터</h2>
          <DTStyle.TableDiv>
            {eventDataList?.map((elem) => (
              <DTStyle.TableElement>
                <h2>{elem?.label}</h2>
                <p>{elem?.value}</p>
              </DTStyle.TableElement>
            ))}
          </DTStyle.TableDiv>
        </Style.AreaDiv>

        {(eventData?.data?.event_type_flag === OFFICIAL ||
          eventData?.data?.event_type_flag === PRELIMINARY) && (
          <Style.AreaDiv>
            <h2>대회 시리즈 데이터</h2>
            <DTStyle.TableDiv>
              {seriesDataList?.map((elem) => (
                <DTStyle.TableElement>
                  <h2>{elem?.label}</h2>
                  <p>{elem?.value}</p>
                </DTStyle.TableElement>
              ))}
            </DTStyle.TableDiv>
          </Style.AreaDiv>
        )}

        <Style.AreaDiv>
          <h2>내용</h2>
          <DTStyle.TableDiv>
            <DTStyle.TableElement>
              <h2>본문</h2>
              <p>{eventContactData}</p>
            </DTStyle.TableElement>
          </DTStyle.TableDiv>
        </Style.AreaDiv>

        {posterImg && (
          <Style.AreaDiv>
            <h2>포스터 이미지</h2>
            <Style.ContentImg src={posterImg} />
          </Style.AreaDiv>
        )}

        {eventImgList?.length > 0 && (
          <Style.AreaDiv>
            <h2>이미지 목록</h2>
            <Style.SliderDiv>
              <button
                onClick={() =>
                  setImgIndex((state) => (state > 0 ? state - 1 : state))
                }
              >
                <img src={imgIndex === 0 ? leftGrey : leftBlack} />
              </button>
              <button
                onClick={() =>
                  setImgIndex((state) =>
                    state < eventImgList?.length - 1 ? state + 1 : state
                  )
                }
              >
                <img
                  src={
                    imgIndex === eventImgList?.length - 1
                      ? rightGrey
                      : rightBlack
                  }
                />
              </button>
            </Style.SliderDiv>
            <Style.ContentImg src={eventImgList[imgIndex]} />
          </Style.AreaDiv>
        )}

        <Style.TabDiv>
          {tabList.map((elem) => (
            <Style.Tab $selected={tab === elem} onClick={() => setTab(elem)}>
              {elem}
            </Style.Tab>
          ))}
        </Style.TabDiv>

        {tab === tabList[0] && (
          <>
            <Style.AreaDiv>
              <h2>게임 데이터</h2>
              <DTStyle.TableDiv>
                {gameDataList?.map((elem) => (
                  <DTStyle.TableElement>
                    <h2>{elem?.label}</h2>
                    <p>{elem?.value}</p>
                  </DTStyle.TableElement>
                ))}
              </DTStyle.TableDiv>
            </Style.AreaDiv>

            {(eventData?.data?.event_type_flag === OFFICIAL ||
              eventData?.data?.event_type_flag === PRELIMINARY ||
              eventData?.data?.event_type_flag === STORE) && (
              <Style.AreaDiv>
                <h2>대회 참가 재료</h2>
                <DTStyle.TableDiv>
                  {ticketDataList?.map((elem) => (
                    <DTStyle.TableElement>
                      <h2>{elem?.label}</h2>
                      <p>{elem?.value}</p>
                    </DTStyle.TableElement>
                  ))}
                </DTStyle.TableDiv>
              </Style.AreaDiv>
            )}
            {eventData?.data?.event_type_flag === FREEROLL && (
              <Style.AreaDiv>
                <h2>참가권 데이터</h2>
                <DTStyle.TableDiv>
                  {publishedTicketDataList?.map((elem) => (
                    <DTStyle.TableElement>
                      <h2>{elem?.label}</h2>
                      <p>{elem?.value}</p>
                    </DTStyle.TableElement>
                  ))}
                </DTStyle.TableDiv>
              </Style.AreaDiv>
            )}
          </>
        )}
        {tab === tabList[1] && (
          <Style.ContainerDiv>
            <Style.AreaDiv>
              <h2>프라이즈 데이터</h2>
              <TStyle.TableDiv>
                <TStyle.TableRow $title>
                  {prizeHeadList.map((elem, index) => (
                    <TStyle.Data $title $size={prizeSizeList[index]}>
                      {elem}
                    </TStyle.Data>
                  ))}
                </TStyle.TableRow>
                {prizeDataList?.map((row, rowIndex) => (
                  <TStyle.TableRow $odd={rowIndex % 2 === 1}>
                    {row?.map((data, dataIndex) => (
                      <TStyle.Data $size={prizeSizeList[dataIndex]}>
                        {data}
                      </TStyle.Data>
                    ))}
                  </TStyle.TableRow>
                ))}
              </TStyle.TableDiv>
            </Style.AreaDiv>

            <Style.AreaDiv>
              <h2>듀레이션 데이터</h2>
              <TStyle.TableDiv>
                <TStyle.TableRow $title>
                  {durationHeadList.map((elem, index) => (
                    <TStyle.Data $title $size={durationSizeList[index]}>
                      {elem}
                    </TStyle.Data>
                  ))}
                </TStyle.TableRow>
                {durationDataList?.map((row, rowIndex) => (
                  <TStyle.TableRow
                    $odd={rowIndex % 2 === 1}
                    $break={row[0] === "break"}
                  >
                    {row.map((data, dataIndex) => (
                      <TStyle.Data
                        $size={durationSizeList[dataIndex]}
                        $break={row[0] === "break"}
                      >
                        {data}
                      </TStyle.Data>
                    ))}
                  </TStyle.TableRow>
                ))}
              </TStyle.TableDiv>
            </Style.AreaDiv>
          </Style.ContainerDiv>
        )}
      </Style.Wrapper>
    </>
  );
};

export default EventDetail;
